
import { Component, Prop, Vue } from 'vue-property-decorator';
import _ from 'lodash';
import Isotope from 'isotope-layout';
import { CreateElement } from 'vue';

const optionsDefault = {
  layoutMode: 'masonry',
  masonry: {
    gutter: 10,
  },
};
function addClass(node: any, classValue: string) {
  if (node.data) {
    const initValue = !node.data.staticClass ? '' : node.data.staticClass + ' ';
    node.data.staticClass = initValue + classValue;
  }
}

function getItemVm(elmt: any) {
  return elmt.__underlying_element;
}
@Component
export default class VueIsotope extends Vue {
  @Prop({ default: () => optionsDefault }) private options!: any;
  @Prop({ default: () => 'item' }) private itemSelector!: string;
  @Prop() private list!: any[];
  private children: any[] = [];
  private vnode: any;
  private oldChidren: any[] = [];
  private removedIndex: any[] = [];
  private displayChildren: any[] = [];
  private filterlistener: any;
  private listeners: any[] = [];
  private isotopeOptions: any = {};
  private iso: any;

  public sort(name: any) {
    let sort = name;
    if (_.isString(name)) {
      sort = { sortBy: name };
    }
    this.arrange(sort);
    this.$emit('sort', name);
  }

  public filter(name: any) {
    const filter = this.buildFilterFunction(name);
    this.arrange({ filter });
    this.$emit('filter', name);
  }

  public unfilter() {
    this.arrange({
      filter: () => {
        return true;
      },
    });
    this.$emit('filter', null);
  }

  public layout(name: any) {
    let layout = name;
    if (_.isString(name)) {
      layout = { layoutMode: name };
    }
    this.arrange(layout);
    this.$emit('layout', layout);
  }

  public arrange(option: any) {
    this.iso.arrange(option);
    this.$emit('arrange', option);
  }

  public shuffle() {
    this.iso.shuffle();
    this.$emit('shuffle');
    this.$emit('sort', null);
  }

  public getFilteredItemElements() {
    return this.iso.getFilteredItemElements();
  }

  public getElementItems() {
    return this.iso.getElementItems();
  }

  private get compiledOptions(): any {
    const options = _.merge({}, this.options, {
      itemSelector: '.' + this.itemSelector,
      isJQueryFiltering: false,
    });

    _.forOwn(options.getSortData, (value: any, key: string) => {
      if (_.isString(value)) {
        options.getSortData[key] = (itemElement: any) => {
          return itemElement[value];
        };
      }
    });

    return options;
  }

  private render(h: CreateElement) {
    const map: any = {};
    const prevChildren: any[] = this.children;
    const rawChildren: any[] = this.$slots.default || [];
    const children: any[] = [];

    rawChildren.forEach((elt: any) => addClass(elt, this.itemSelector));

    for (const c of rawChildren) {
      if (c.tag) {
        if (c.key != null && String(c.key).indexOf('__vlist') !== 0) {
          children.push(c);
          map[c.key] = c;
        }
      }
    }

    const displayChildren = (this.displayChildren = [...children]);

    if (prevChildren) {
      for (let i = 0; i < prevChildren.length; i++) {
        const c = prevChildren[i];
        if (!map[c.key]) {
          displayChildren.splice(i, 0, c);
          this.removedIndex.push(i);
        }
      }
    }

    return h('div', undefined, displayChildren);
  }

  private mounted() {
    const options = _.merge({}, this.compiledOptions);
    const update = (object: any) => {
      _.forOwn(object, (value, key) => {
        object[key] = (itemElement: any) => {
          const res = getItemVm(itemElement);
          return value.call(this, res.vm, res.index);
        };
      });
    };
    update(options.getSortData);
    update(options.getFilterData);
    this.isotopeOptions = options;
    if (options.filter) {
      options.filter = this.buildFilterFunction(options.filter);
    }

    this.$nextTick(() => {
      this.link();
      this.listen();
      const iso = new Isotope(this.$el as HTMLElement, options) as any;

      iso._requestUpdate = () => {
        if (iso._willUpdate) {
          return;
        }

        iso._willUpdate = true;
        this.$nextTick(() => {
          iso.arrange();
          iso._willUpdate = false;
        });
      };
      this.iso = iso;
    });
  }

  private beforeDestroy() {
    _.forEach(this.listeners, unlisten => {
      unlisten();
    });
    if (this.filterlistener) {
      this.filterlistener();
    }
    this.iso = null;
  }

  private beforeUpdate() {
    this.oldChidren = Array.prototype.slice.call(this.$el.children);
  }
  private updated() {
    if (!this.iso) {
      return;
    }

    const newChildren = [...this.$el.children];
    const added = _.difference(newChildren, this.oldChidren);
    const removed = this.removedIndex.map(index => this.$el.children[index]);

    this.cleanupNodes();
    this.link();

    if (!removed.length && !added.length) {
      return;
    }

    this.listen();

    this.iso.remove(removed);
    this.iso.insert(added);
    this.iso._requestUpdate();
  }
  private cleanupNodes() {
    this.removedIndex.reverse();
    this.removedIndex.forEach(index => this.vnode.children.splice(index, 1));
  }

  private link() {
    const slots = this.$slots.default || [];
    slots.forEach((slot, index) => {
      const elmt = slot.elm as any;
      if (elmt) {
        elmt.__underlying_element = { vm: this.list[index], index };
      }
    });
  }

  private listen() {
    const sortedSortData = _.map(this.compiledOptions.getSortData, (sort: any) => {
      return _.map(this.list, collectionElement => {
        return this.$watch(
          () => {
            return sort(collectionElement);
          },
          () => {
            this.iso.updateSortData();
            this.iso._requestUpdate();
          }
        );
      });
    });
    const flat = _.flatten(sortedSortData);
    this.listeners = _.values(flat);
  }

  private buildFilterFunction(name: any) {
    const filter = this.isotopeOptions.getFilterData[name];
    this.filterlistener = this.$watch(
      () => {
        return _.map(this.list, (el, index) => this.options.getFilterData[name](el, index));
      },
      () => {
        this.iso._requestUpdate();
      }
    );
    return filter;
  }
}
